import { useNavigate } from "react-router-dom";
import { MoreHorizontal, Eye, Pencil, Archive, RotateCcw, Flame } from "lucide-react";

import { relationshipLabel } from "@/config/crmOptions";
import { fmtDate } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const relBadgeClass = (rel) => {
  switch (rel) {
    case "buyer":
    case "seller": return "bg-emerald-100 text-emerald-800";
    case "past_client": return "bg-brand-navy/10 text-brand-navy";
    case "lead":
    case "prospect": return "bg-amber-100 text-amber-800";
    case "referral_partner": return "bg-violet-100 text-violet-800";
    default: return "bg-secondary text-muted-foreground";
  }
};

export const ContactTable = ({ contacts, onEdit, onArchive, onRestore, onNewLead }) => {
  const navigate = useNavigate();

  return (
    <div className="overflow-x-auto rounded-xl border border-border bg-card shadow-card">
      <Table data-testid="contact-table">
        <TableHeader>
          <TableRow className="bg-secondary/50">
            <TableHead>Name</TableHead>
            <TableHead>Relationship</TableHead>
            <TableHead>Source</TableHead>
            <TableHead>Last Contact</TableHead>
            <TableHead>Next Follow-Up</TableHead>
            <TableHead>Phone</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Tags</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {contacts.map((c) => (
            <TableRow
              key={c.id}
              className="cursor-pointer"
              data-testid={`contact-row-${c.id}`}
              onClick={() => navigate(`/crm/${c.id}`)}
            >
              <TableCell className="font-medium text-foreground">
                {c.first_name} {c.last_name}
                {c.status === "archived" && <Badge variant="outline" className="ml-2 text-xs">Archived</Badge>}
              </TableCell>
              <TableCell>
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${relBadgeClass(c.relationship_type)}`}>
                  {relationshipLabel(c.relationship_type)}
                </span>
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">{c.lead_source?.name || "—"}</TableCell>
              <TableCell className="text-sm text-muted-foreground">{fmtDate(c.last_contact_at)}</TableCell>
              <TableCell className="text-sm text-muted-foreground">{fmtDate(c.next_followup_at)}</TableCell>
              <TableCell className="text-sm text-muted-foreground">{c.phone || "—"}</TableCell>
              <TableCell className="text-sm text-muted-foreground">{c.email || "—"}</TableCell>
              <TableCell>
                <div className="flex flex-wrap gap-1">
                  {(c.tags || []).slice(0, 3).map((t) => (
                    <Badge key={t.id} variant="secondary" className="text-[10px]">{t.name}</Badge>
                  ))}
                  {(c.tags || []).length > 3 && <span className="text-xs text-muted-foreground">+{c.tags.length - 3}</span>}
                </div>
              </TableCell>
              <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="rounded-md p-1.5 hover:bg-secondary" data-testid={`contact-actions-${c.id}`} aria-label="Contact actions">
                      <MoreHorizontal className="h-4 w-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-44">
                    <DropdownMenuItem onClick={() => navigate(`/crm/${c.id}`)} data-testid={`contact-view-${c.id}`}>
                      <Eye className="h-4 w-4" /> View
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onEdit(c)} data-testid={`contact-edit-${c.id}`}>
                      <Pencil className="h-4 w-4" /> Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onNewLead(c)} data-testid={`contact-newlead-${c.id}`}>
                      <Flame className="h-4 w-4" /> New Lead
                    </DropdownMenuItem>
                    {c.status === "archived" ? (
                      <DropdownMenuItem onClick={() => onRestore(c)} data-testid={`contact-restore-${c.id}`}>
                        <RotateCcw className="h-4 w-4" /> Restore
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem onClick={() => onArchive(c)} className="text-destructive" data-testid={`contact-archive-${c.id}`}>
                        <Archive className="h-4 w-4" /> Archive
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
